import { useLayoutEffect, useRef } from 'react';

import { useSubscription } from '../views/Subscription/SubscriptionProvider';

/**
 * Vertical gap between the scroll container top and the subscription step section
 * (below fixed navbar).
 */
export const SUBSCRIPTION_SECTION_SCROLL_GAP_PX = 20;

export function scrollSubscriptionSectionIntoView(scrollContainer, sectionElement) {
  if (!scrollContainer || !sectionElement) {
    return;
  }
  const cRect = scrollContainer.getBoundingClientRect();
  const sRect = sectionElement.getBoundingClientRect();
  const nextTop =
    sRect.top - cRect.top + scrollContainer.scrollTop - SUBSCRIPTION_SECTION_SCROLL_GAP_PX;
  scrollContainer.scrollTo({
    top: Math.max(0, nextTop),
    behavior: 'smooth',
  });
}

/**
 * Scrolls the subscription layout container so `sectionRef` sits
 * SUBSCRIPTION_SECTION_SCROLL_GAP_PX under the top of the scroll area (flush under the navbar).
 *
 * Skips the first effect run (initial mount / page load) so we only scroll when `extraDeps`
 * change after the user moves between substeps (e.g. review → checkout, carousel slide).
 */
export function useSubscriptionStepScroll(sectionRef, ...extraDeps) {
  const { contentScrollRef } = useSubscription();
  const isInitialLayout = useRef(true);

  useLayoutEffect(() => {
    if (isInitialLayout.current) {
      isInitialLayout.current = false;
      return undefined;
    }

    const run = () => {
      scrollSubscriptionSectionIntoView(contentScrollRef.current, sectionRef.current);
    };
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    return () => cancelAnimationFrame(id);
  }, [contentScrollRef, sectionRef, ...extraDeps]);
}
